import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Collapse from '@material-ui/core/Collapse';
import LinearProgress from '@material-ui/core/LinearProgress';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';
import EmailIcon from '@material-ui/icons/Email';
import SmsIcon from '@material-ui/icons/Sms';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { IAdHocSendEvent } from '../../../lib/types/communication.types';
import { OutlineButton } from '../../_core/_ui/buttons.component';
import { SearchField } from '../../_core/_ui/search-field.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { getAdHocSendEventItems } from '../../../lib/services/communication.service';
import { useDebouncedValue } from '../../../lib/hooks/useDebouncedValue';
import { dangerTextColor } from '../../../lib/theme';
import { AssessmentSessionSummary } from '../../Session/assessment-session-summary.component';
import { HistoryEventItem } from './history-event-item.component';
import { HistoryPager } from './history-pager.component';
import { sendEventProgress } from './history-panel.helpers';
import { messages } from './history-panel.messages';
import { HISTORY_ITEMS_PAGE_SIZE } from './history-panel.constants';
import {
  ChipRow,
  EventBody,
  EventCard,
  EventHeader,
  ExpandToggle,
  ItemSearchBar,
  MethodAvatar,
  MethodAvatarGroup,
  SendingSummary,
  Subject
} from './history-panel.styles';
import { StatusChip } from '../../_core/_ui/status-chip.styles';

interface IEventRowProps {
  event: IAdHocSendEvent;
  assessmentId: string;
  onResend: (communicationGroupId: string) => Promise<unknown>;
}

export const HistoryEventRow: React.FC<IEventRowProps> = ({ event, assessmentId, onResend }) => {
  const [open, setOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | undefined>();
  const [itemQuery, setItemQuery] = useState('');
  const [itemPage, setItemPage] = useState(0);
  const debouncedItemQuery = useDebouncedValue(itemQuery.trim(), 300);

  useEffect(() => { setItemPage(0); }, [debouncedItemQuery]);

  // Recipient rows are fetched only while the row is open (skip otherwise), so
  // a long history list doesn't fan out a query per event.
  const { items, total: itemTotal, loading: itemsLoading } = getAdHocSendEventItems({
    assessmentId,
    communicationGroupId: event._id,
    search: debouncedItemQuery,
    offset: itemPage * HISTORY_ITEMS_PAGE_SIZE,
    limit: HISTORY_ITEMS_PAGE_SIZE,
    skip: !open
  });

  const dateLabel = useMemo(
    () => (event.createdAt ? dayjs(event.createdAt).format('MMM D, YYYY h:mm A') : '—'),
    [event.createdAt]
  );

  // Withhold the final tally + retry affordance until every row settles; while
  // unsettled the header shows only the in-progress delivery summary.
  const progress = useMemo(() => sendEventProgress(event), [event]);

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    setRetryError(undefined);
    try {
      // event._id is the communicationGroupId the resend mutation takes.
      await onResend(event._id);
    } catch (err) {
      console.error('resendAdHocSendEventFailed failed:', err);
      // `||` not `??`: an empty-string `.message` should fall through to the
      // user-facing fallback (REACT_CONVENTIONS.md "User-facing copy fallbacks").
      const message = err && (err as { message?: string }).message;
      setRetryError(message || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }, [onResend, event._id]);

  const showItemSearch = open && (itemTotal > HISTORY_ITEMS_PAGE_SIZE || !!debouncedItemQuery);

  let body;
  if (itemsLoading && items.length === 0) {
    body = (
      <Box px={2.5} py={1.5} display="flex" justifyContent="center">
        <CircularProgress size={18} />
      </Box>
    );
  } else if (items.length === 0) {
    body = (
      <Box px={2.5} py={1.5}>
        <Typography variant="caption" color="textSecondary">
          {debouncedItemQuery ? 'No recipients match your search.' : 'No recipients.'}
        </Typography>
      </Box>
    );
  } else {
    body = (
      <>
        <List dense disablePadding>
          {items.map((item) => <HistoryEventItem key={item._id} item={item} />)}
        </List>
        {itemTotal > HISTORY_ITEMS_PAGE_SIZE && (
          <HistoryPager
            page={itemPage}
            pageSize={HISTORY_ITEMS_PAGE_SIZE}
            total={itemTotal}
            onPageChange={setItemPage}
          />
        )}
      </>
    );
  }

  return (
    <EventCard>
      <EventHeader role="button" aria-expanded={open} onClick={() => setOpen((s) => !s)}>
        <MethodAvatarGroup>
          {event.hasEmail && (
            <MethodAvatar>
              <EmailIcon fontSize="small" />
            </MethodAvatar>
          )}
          {event.hasSms && (
            <MethodAvatar>
              <SmsIcon fontSize="small" />
            </MethodAvatar>
          )}
        </MethodAvatarGroup>
        <Box flexGrow={1} minWidth={0}>
          {/* title is the user-authored identifier; subject is the legacy
              fallback for events sent before title existed; the (no subject)
              copy remains as a final fallback for any pre-feature record that
              had neither stamped. */}
          <Subject>
            {event.title || event.subject || (event.hasSms && !event.hasEmail ? 'Text message' : '(no subject)')}
          </Subject>
          <Typography variant="caption" color="textSecondary" component="div">
            {dateLabel}
          </Typography>
          {event.assessmentSession && (
            <Typography variant="caption" color="textSecondary" component="div">
              <AssessmentSessionSummary session={event.assessmentSession} />
            </Typography>
          )}
          {progress.settled ? (
            <>
              <ChipRow>
                {event.isTest && (
                  <StatusChip label="Test" size="small" $tone="info" />
                )}
                {event.sentCount > 0 && (
                  <StatusChip label={`${event.sentCount} sent`} size="small" $tone="success" />
                )}
                {event.failedCount > 0 && (
                  <StatusChip label={`${event.failedCount} failed`} size="small" $tone="danger" />
                )}
                {event.skippedCount > 0 && (
                  <StatusChip label={`${event.skippedCount} skipped`} size="small" $tone="neutral" />
                )}
              </ChipRow>
              {event.failedCount > 0 && (
                <Box mt={1} display="flex" alignItems="center" gridGap={8} flexWrap="wrap">
                  <OutlineButton
                    size="small"
                    disabled={retrying}
                    onClick={(e) => { e.stopPropagation(); handleRetry(); }}
                  >
                    {retrying ? 'Retrying…' : `Retry ${event.failedCount} failed`}
                  </OutlineButton>
                  {retryError && (
                    <Typography variant="caption" style={{ color: dangerTextColor }}>{retryError}</Typography>
                  )}
                </Box>
              )}
            </>
          ) : (
            <SendingSummary aria-live="polite">
              <CircularProgress size={12} />
              <Typography variant="caption" color="textSecondary">
                <IntlMsg
                  {...messages.sendingSummary}
                  values={{ delivered: progress.delivered, total: progress.total }}
                />
              </Typography>
            </SendingSummary>
          )}
        </Box>
        <ExpandToggle
          size="small"
          $open={open}
          onClick={(e) => { e.stopPropagation(); setOpen((s) => !s); }}
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          <ExpandMoreIcon />
        </ExpandToggle>
      </EventHeader>
      <Collapse in={open} unmountOnExit>
        <EventBody>
          {showItemSearch && (
            <ItemSearchBar>
              <SearchField
                value={itemQuery}
                onChange={setItemQuery}
                placeholder="Search recipients"
                ariaLabel="Search recipients in this send"
              />
            </ItemSearchBar>
          )}
          {/* In-flight bar for recipient page/search refetches over existing
              rows; the centered spinner in `body` covers the empty case. */}
          {itemsLoading && items.length > 0 && <LinearProgress />}
          {body}
        </EventBody>
      </Collapse>
    </EventCard>
  );
};
